import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { reduxForm, Field, formValueSelector } from 'redux-form';
import { connect } from 'react-redux';
import moment from 'moment';
import Joi from 'joi-full';
import Autosuggest from 'react-autosuggest';
import { pick, debounce, range, isUndefined, get } from 'lodash';
import { compose, withHandlers, withPropsOnChange } from 'recompose';
import Tooltip from '../../common/tooltip';
import UploadLogo from './UploadLogo';
import CancelButtonLink from '../../common/buttons/cancelButtonLink';
import SaveButton from '../../common/buttons/saveButton';
import NotificationMethods from '../../common/notificationMethods';
import { GoogleReviewsSection } from './components/GoogleReviews';
import UploadAvatar from '../../common/uploadPhotoForm/uploadAvatar';
import Schedules from '../../common/schedulesForm/schedulesFormContainer';
import AddressForm from '../../common/addressForm';
import { prepareSchemasByCountry } from '../../common/addressForm/schema';
import { syncValidateData } from '../../common/validator';
import config from '../../../config';
import Input from '../../common/input';
import TextArea from '../../common/textarea';
import CheckboxSwitch from '../../common/checkboxSwitch';
import CurrencyInput from '../../common/currencyInput';
import {
  prepareScheduleForSaving,
  prepareMinsForSaving,
  getSchedulesValidationErrors,
  prepareDaysForSaving,
} from '../../common/helpers/schedules';
import { RoleAllowAdmin, RoleDenyAdmin } from '../../auth/roleAware';
import { SHOP_TYPE, COUNTRY_NAME_BY_CODE, brandSupportedLanguagesOption } from './constants';
import { trimFunction } from '../../common/trimData';
import BarberSettings from './barberSettings';
import CommissionSplit from './commissionSplit';
import {
  getIsAdminRole,
  getIsPermissionPasscodeRequired,
  activeShopMinInterval,
  getIsReadOnlyAdminUser,
  activeShopDailyPayrollEnabled,
  activeShopCommissionSplitsEnabled,
} from '../../auth/selectors';
import { getIsLogoUploadAllowed } from './selectors';
import CountryCodePhoneInputRedux from '../../common/countryCodePhoneInputRedux';
import Days from '../../common/schedulesForm/daysContainer';
import Select from '../../common/select';
import LoginButton from './LoginButton';
import ImpersonateUser from '../../admin/shared/ImpersonateUser';
import LogoutUserSessions from '../../admin/shared/LogoutUserSessions';
import PermissionPasscode from '../../common/PermissionPasscode';
import { minsToDaysHoursMins } from '../../common/helpers/times';
import { payrollKindShape } from '../settings/commission/shapes';
import { DISCOUNT_PAYOUT_OPTIONS, PAYROLL_KINDS } from '../settings/commission/constants';
import { CurrencySelect } from '../../common/formFields/CurrencySelect';
import confirmModal from '../../common/confirmModal';
import BrandField from './containers/BrandField';
import ShopLanguageSettings from './components/ShopLanguageSettings';
import { getBrand } from '../../admin/brands/reducers';
import getLanguageOptionsByCodes from '../../common/LanguageMultiSelect/getLanguageOptionsByCodes';
import { resetAutoPayoutSuggestionModalState } from '../suggestAutoPayoutModal/actions';
import { flagNames, withInjectFeatureFlagsToProps } from '../../../modules/infra/feature-flags';
import OptionInput from '../../common/optionInput';
import { PrepaymentSettingsContainer } from './prepayment-settings/prepayment-settings.container';
import WalkInListUrlDisplay from './components/WalkInListUrlDisplay';

const regexMatchError = (err) => ({
  language: { string: { regex: { base: ` ${err}` } } },
});

const allTimezones = moment.tz.names();
const dateFormatOptions = [
  {
    label: 'USA',
    value: config.dateFormat,
  },
  {
    label: 'UK/Europe',
    value: 'DD/MM/YYYY',
  },
];

const baseSchema = {
  brandName: Joi.string().min(1).label('Brand Name').required(),
  name: Joi.string().min(3).label('Shop name').required(),
  alias: Joi.string().allow('', null).min(3).label('Shop alias').optional(),
  email: Joi.string().allow('', null).email({ minDomainAtoms: 2 }).label('Email'),
  phone: Joi.string().allow('', null).label('Phone'),
  yelpID: Joi.string()
    .allow('', null)
    .regex(/^[a-zA-Z0-9-]+$/)
    .label('Yelp ID')
    .optional()
    .options(regexMatchError('can only contain numbers, letters and dashes')),
  timezone: Joi.any()
    .valid(allTimezones)
    .allow('', null)
    .optional()
    .options({
      language: {
        any: {
          allowOnly: ' must be a valid timezone',
        },
      },
    }),
  currency: Joi.string().optional(),
  commissionSplits: Joi.boolean().optional(),
  commissionSplitsPercentFee: Joi.when('commissionSplits', {
    is: true,
    then: Joi.number()
      .min(0)
      .max(100)
      .required()
      .label('Auto Commission Split Fee')
      .options({
        language: { number: { base: ' value  is required.' } },
      }),
    otherwise: Joi.number().min(0).max(100).optional().allow('', null),
  }),
  commissionSplitsCashToBarbersServices: Joi.boolean().optional(),
  commissionSplitsCashToBarbersProducts: Joi.boolean().optional(),
  commissionSplitsShopPaysFees: Joi.boolean().optional(),
  commissionPayoutPeriodInDays: Joi.when('commissionSplits', {
    is: true,
    then: Joi.number().valid(1, 7, 14, 21).required(),
  }),
  commissionPayoutDayOfWeek: Joi.when('commissionSplits', {
    is: true,
    then: Joi.number().min(1).max(7).optional(),
  }),
  giftCardEnabled: Joi.boolean().optional().allow(null),
  customerGiftCardEnabled: Joi.boolean().optional().allow(null),
  sendGCNotificationsToCommissionBarbers: Joi.boolean().optional().allow(null),
  tipTier1: Joi.number().min(0).max(100).optional(),
  tipTier2: Joi.number().min(0).max(100).optional(),
  tipTier3: Joi.number().min(0).max(100).optional(),
  tipTier4: Joi.number().min(0).optional(),
  flatTipTier1: Joi.number().min(0).optional(),
  flatTipTier2: Joi.number().min(0).optional(),
  flatTipTier3: Joi.number().min(0).optional(),
  flatTipTier4: Joi.number().min(0).optional(),
  roundUpTips: Joi.boolean().optional(),
  flatTipsThreshold: Joi.number().min(0).optional(),
  paymentProcessorReader: Joi.string().valid(['stripe']),
  readerLocationId: Joi.when('webReader', {
    is: true,
    then: Joi.string().required(),
    otherwise: Joi.string().optional().allow('', null),
  }),
  prepayOnlyThreshold: Joi.number().optional(),
  tipOnProduct: Joi.boolean().optional(),
  calculateTaxFromNetAmount: Joi.boolean().optional(),
  dailyPayrollTipFee: Joi.when('dailyPayroll', {
    is: true,
    then: Joi.number()
      .min(0)
      .max(100)
      .required()
      .label('Daily Shop Payout Fee')
      .options({
        language: { number: { base: ' value  is required.' } },
      }),
    otherwise: Joi.number().min(0).max(100).optional().allow('', null),
  }),
  barberSpecificGC: Joi.boolean().optional(),
  includeChargeLinkInNotification: Joi.boolean().optional(),
  dateFormat: Joi.string().optional().allow('', null),
  brandId: Joi.string().required(),
  advanceBookDays: Joi.number().min(1).max(365).required().label('Book in advance'),
  supportedLanguages: Joi.array(),
  settings: Joi.object({
    requiresPartialDeposits: Joi.boolean(),
    depositType: Joi.when('requiresPartialDeposits', {
      is: true,
      then: Joi.string()
        .valid('percentage', 'amount')
        .required()
        .label('Deposit Type')
        .options({
          language: { any: { allowOnly: ' must be either percentage or amount' } },
        }),
      otherwise: Joi.string().allow(null).optional(),
    }),
    depositAmount: Joi.when('settings.depositType', {
      is: 'amount',
      then: Joi.number()
        .min(1)
        .required()
        .label('Deposit Amount')
        .options({
          language: {
            number: {
              base: 'Please enter a valid deposit amount',
              min: 'Deposit amount must be at least $1',
            },
          },
        }),
      otherwise: Joi.number().allow(null).optional(),
    }),
    depositPercentage: Joi.when('settings.depositType', {
      is: 'percentage',
      then: Joi.number()
        .min(1)
        .max(100)
        .required()
        .label('Deposit Percentage')
        .options({
          language: {
            number: {
              base: 'Please enter a valid deposit percentage between 1 and 100',
              min: 'Deposit percentage must be at least 1%',
              max: 'Deposit percentage cannot exceed 100%',
            },
          },
        }),
      otherwise: Joi.number().allow(null).optional(),
    }),
    flexCtaOnCom: Joi.boolean().required(),
  }),
};

const prepayOnlyThresholdOptions = range(0, 735, 15).map((mins) => ({
  label: minsToDaysHoursMins(mins) || '0 mins',
  value: mins,
}));

const schemaByCountry = prepareSchemasByCountry(baseSchema);

const validate = (model) => {
  const { address } = model;
  const country = address ? address.country : COUNTRY_NAME_BY_CODE.US;
  const validationResult = syncValidateData(model, schemaByCountry[country]);

  // If validation returns true, there are no errors
  if (validationResult === true) {
    return {};
  }

  // If we have a general error, return it
  if (validationResult._error) {
    return { _error: validationResult._error };
  }

  // Process nested errors (like settings.depositType)
  const errors = {};
  Object.keys(validationResult).forEach((key) => {
    if (key.includes('.')) {
      // For nested paths like 'settings.depositType'
      const [parent, child] = key.split('.');
      if (!errors[parent]) {
        errors[parent] = {};
      }
      errors[parent][child] = validationResult[key];
    } else {
      errors[key] = validationResult[key];
    }
  });

  return errors;
};

const fields = [
  'avatar',
  'name',
  'alias',
  'website',
  'phone',
  'email',
  'currency',
  'heading',
  'yelpID',
  'instagram',
  'timezone',
  'inventoryAlertEmail',
  'inventoryAlertPhone',
  'kind',
  'notifications',
  'customerReminder',
  'schedules',
  'days_off',
  'address',
  'bookNoPay',
  'overrideBarberSettings',
  'tipEnabled',
  'earlyTipping',
  'lateTipping',
  'canCustomerCancel',
  'advanceBookDays',
  'advanceBookMins',
  'advanceCancelMins',
  'minInterval',
  'location',
  'adultsOnly',
  'allowMultipleServices',
  'creditCardFlatFee',
  'creditCardPercentFee',
  'presentCreditCardFlatFee',
  'presentCreditCardPercentFee',
  'amexCreditCardFlatFee',
  'amexCreditCardPercentFee',
  'amexPresentCreditCardFlatFee',
  'tapToPayFlatFee',
  'amexPresentCreditCardPercentFee',
  'customerBookingFee',
  'waitingListFee',
  'creditCardFeeEnabled',
  'enabled',
  'verified',
  'allowBookWithAnyBarber',
  'instantPayoutPercentageFee',
  'customerBookingFeeLimitEnabled',
  'customerBookingFeeBottomLimit',
  'customerBookingFeeTopLimit',
  'commissionSplits',
  'commissionSplitsCashToBarbersServices',
  'commissionSplitsCashToBarbersProducts',
  'commissionPayoutPeriodInDays',
  'commissionPayoutDayOfWeek',
  'grossUpFees',
  'waitingListEnabled',
  'rewardsEnabled',
  'kioskEnabled',
  'cashDrawerEnabled',
  'defaultCashDrawerBalance',
  'squireEcosystem',
  'googleEcosystem',
  'sendEmailReports',
  'sendMonthlyFeesReport',
  'readerlessPos',
  'webReader',
  'readerLocationId',
  'noDefaultTip',
  'cashTipsEnabled',
  'readerTipsEnabled',
  'giftCardTipsEnabled',
  'splitPaymentsEnabled',
  'chargeNoShows',
  'productSalesToShop',
  'walkInOnly',
  'calculateHourlyWages',
  'commissionSplitsShopPaysFees',
  'tipTier1',
  'tipTier2',
  'tipTier3',
  'tipTier4',
  'flatTipTier1',
  'flatTipTier2',
  'flatTipTier3',
  'flatTipTier4',
  'flatTipsThreshold',
  'roundUpTips',
  'allowBookAndReserve',
  'allowInPersonPayments',
  'requiresPartialDeposits',
  'requestCardForInPersonPayments',
  'logo',
  'giftCardEnabled',
  'customerGiftCardEnabled',
  'sendGCNotificationsToCommissionBarbers',
  'bookingCodeEnabled',
  'legacyCommanderUser',
  'paymentProcessorReader',
  'boothRentalEnabled',
  'boothRentalFee',
  'timeTrackingEnabled',
  'permissionPasscodeEnabled',
  'prepayOnlyThreshold',
  'tipOnProduct',
  'calculateTaxFromNetAmount',
  'dailyPayroll',
  'dailyPayrollTipFee',
  'commissionSplitsPercentFee',
  'noExtrasForBookNoPay',
  'requiresPrepayment',
  'paymentDepositsEnabled',
  'barberSpecificGC',
  'requiresWaiver',
  'waiverLanguage',
  'useQuickBooks',
  'includeChargeLinkInNotification',
  'applyCNPRateToChargeLink',
  'includeTaxInTipCalculation',
  'dateFormat',
  'mobileAppCta',
  'payoutDiscountSplit',
  'brandName',
  'brandId',
  'supportedLanguages',
  'groupAppointmentsEnabled',
  'clientReviewsEnabled',
  'legacyCustomerBookingFeeCalculation',
  'settings',
];

class ShopForm extends Component {
  static propTypes = {
    handleSubmit: PropTypes.func.isRequired,
    saveUpdatedShop: PropTypes.func.isRequired,
    change: PropTypes.func.isRequired,
    submitting: PropTypes.bool.isRequired,
    currency: PropTypes.string.isRequired,
    schedules: PropTypes.object,
    user: PropTypes.object.isRequired,
    cancelHref: PropTypes.string,
    disabled: PropTypes.bool,
    isLogoUploadAllowed: PropTypes.bool,
    isRental: PropTypes.bool.isRequired,
    initialValues: PropTypes.object.isRequired,
    shopUser: PropTypes.object.isRequired,
    kioskEnabled: PropTypes.bool.isRequired,
    payrollKind: payrollKindShape.isRequired,
    payrollEnabled: PropTypes.bool.isRequired,
    googleReviewEnabled: PropTypes.bool.isRequired,
    isAdminShopForm: PropTypes.bool.isRequired,
    isReadOnlyAdmin: PropTypes.bool.isRequired,
    commissionSplitsEnabled: PropTypes.bool.isRequired,
    isPermissionPasscodeRequired: PropTypes.bool.isRequired,
    canCustomerCancel: PropTypes.bool.isRequired,
    minInterval: PropTypes.number,
    daysOff: PropTypes.arrayOf(PropTypes.object),
    resetAutoPayoutSuggestionModal: PropTypes.func.isRequired,
    settings: PropTypes.object.isRequired,
    shopLdFlags: PropTypes.object.isRequired,
    canCustomerEnrollToWalkInList: PropTypes.bool,
  };

  static defaultProps = {
    disabled: false,
    isLogoUploadAllowed: false,
    cancelHref: null,
    schedules: null,
    shopUser: {},
    minInterval: null,
    daysOff: null,
    shopLdFlags: {},
    canCustomerEnrollToWalkInList: false,
  };

  static onKeyDown(e) {
    const keyCode = e.keyCode || e.charCode;
    if (keyCode === config.ENTER_KEY_CODE) {
      e.preventDefault();
    }
  }

  // When suggestion is clicked, Autosuggest needs to populate the input field
  // based on the clicked suggestion. Teach Autosuggest how to calculate the
  // input value for every given suggestion.
  static getSuggestionValue(suggestion) {
    return suggestion;
  }

  static renderSuggestion(suggestion) {
    return <span>{`${moment.tz(suggestion).format('Z')} ${suggestion}`}</span>;
  }

  constructor(props) {
    super(props);
    this.state = {
      currentScheduleError: false,
      futureScheduleError: false,
      timezoneSuggestions: [],
      errors: null,
    };
    this.timezoneField = this.setupTimezoneField();
    this.debouncedLoadSuggestions = debounce(this.getSuggestions, 300);
    this.saveBind = (...args) => this.save(...args);
    this.onChangeBind = (...args) => this.onChange(...args);
    this.onSuggestionsFetchRequestedBind = (...args) => this.onSuggestionsFetchRequested(...args);
    this.onSuggestionsClearRequestedBind = (...args) => this.onSuggestionsClearRequested(...args);
  }

  // Autosuggest will call this function every time you need to update suggestions.
  onSuggestionsFetchRequested({ value }) {
    this.debouncedLoadSuggestions(value);
  }

  // Autosuggest will call this function every time you need to clear suggestions.
  onSuggestionsClearRequested() {
    this.setState({
      timezoneSuggestions: [],
    });
  }

  onChange(e, { newValue }) {
    this.props.change('timezone', newValue);
  }

  setupTimezoneField() {
    return (field) => {
      let validationError = null;
      // TODO: Restore placeholder if Safari bug is fixed
      const timezoneInputProps = {
        name: 'timezone',
        value: field.input.value || '',
        onChange: this.onChangeBind,
        onKeyDown: this.onKeyDown,
        onBlur: field.input.onBlur,
        className: 'c-input',
        placeholder: '',
      };

      if (field.meta.touched && field.meta.error) {
        validationError = <div className="c-input-error-message">{field.meta.error}</div>;
      }

      return (
        <div className="u-position-relative">
          <div className="c-label-new">Timezone</div>
          <Autosuggest
            suggestions={field.timezoneSuggestions}
            onSuggestionsFetchRequested={this.onSuggestionsFetchRequestedBind}
            onSuggestionsClearRequested={this.onSuggestionsClearRequestedBind}
            getSuggestionValue={ShopForm.getSuggestionValue}
            renderSuggestion={ShopForm.renderSuggestion}
            inputProps={timezoneInputProps}
          />
          {validationError}
        </div>
      );
    };
  }

  // Teach Autosuggest how to calculate suggestions for any given input value.
  getSuggestions(value) {
    const inputValue = value.trim().toLowerCase();
    const inputLength = inputValue.length;
    const result = inputLength === 0 ? [] : allTimezones.filter((zone) => zone.toLowerCase().indexOf(inputValue) > -1);

    this.setState({
      timezoneSuggestions: result,
    });
  }

  async save(data) {
    const question =
      'Are you sure you want to override "Online Tipping", ' +
      // eslint-disable-next-line max-len
      '"Allow multiple services", "Customer Cancellations" and "Past Appointment Editing" settings on all barbers working in the shop?';

    if (data.overrideBarberSettings) {
      const confirmed = await confirmModal({
        type: 'danger',
        title: 'Override barber settings',
        message: question,
      });

      if (!confirmed) {
        return false;
      }
    }

    const reminder =
      'Do not forget to: ' +
      '1) Switch the POS reader to point to the right account. Otherwise, the shop will not be able to use the POS.' +
      '2) Adjust transaction fees accordingly';

    if (this.props.initialValues.commissionSplits !== data.commissionSplits) {
      const confirmed = await confirmModal({
        type: 'danger',
        title: "Don't forget",
        message: reminder,
      });

      if (!confirmed) {
        return false;
      }
    }

    const model = pick(data, fields);
    const schedulesErrors = getSchedulesValidationErrors(model.schedules);
    this.setState(schedulesErrors);
    if (schedulesErrors.currentScheduleError || schedulesErrors.futureScheduleError) {
      return Promise.reject();
    }

    model.settings = data.settings || {};
    model.kind = parseInt(model.kind, 10);
    model.schedules = prepareScheduleForSaving(model.schedules);
    model.days_off = prepareDaysForSaving(model.days_off);
    model.advanceBookMins = prepareMinsForSaving(model.advanceBookMins);
    model.advanceCancelMins = prepareMinsForSaving(model.advanceCancelMins);
    model.notifications.reminder.time = prepareMinsForSaving(model.notifications.reminder.time);
    model.customerReminder.barber_reminder.time = prepareMinsForSaving(model.customerReminder.barber_reminder.time);
    model.customerReminder.reminder.time = prepareMinsForSaving(model.customerReminder.reminder.time);

    if (model.location) {
      model.location = { ...model.location, ...{ type: 'Point' } };
    }
    if (model.kind !== SHOP_TYPE.rental) {
      model.productSalesToShop = false;
    }
    if (!model.website) {
      model.website = '';
    }
    if (!Number(model.instantPayoutPercentageFee) && !this.props.initialValues.id) {
      model.instantPayoutPercentageFee = undefined;
    }
    if (!model.kioskEnabled) {
      model.prepayOnlyThreshold = 0;
    }

    if (!this.props[flagNames.AUTOPAYOUT_SELF_SERVE]) {
      if (!model.commissionSplits) {
        model.commissionSplitsCashToBarbersServices = false;
        model.commissionSplitsCashToBarbersProducts = false;
        model.commissionSplitsShopPaysFees = false;
        model.commissionPayoutDayOfWeek = null;
        model.commissionPayoutPeriodInDays = null;
        model.commissionSplitsPercentFee = 0;
      }

      if (!model.dailyPayroll) {
        model.dailyPayrollTipFee = 0;
      }
    }

    model.waiverLanguage = 'en';
    if (model.requiresWaiver && data.isFrenchWaiver) {
      model.waiverLanguage = 'fr';
    }

    // temporary fix for broken qa - default value was not assigned to a new column
    if (model.settings.walkInListEnabled === null) {
      model.settings.walkInListEnabled = false;
    }

    const paymentDepositsEnabled = this.props.shopLdFlags?.['payment-deposits-enabled'];

    if (paymentDepositsEnabled) {
      // Map selectedPrepaymentOption to the three API fields
      const prepayOption = data.selectedPrepaymentOption;
      if (prepayOption === 'partial-deposit') {
        model.requiresPartialDeposits = true;
        model.requiresPrepayment = false;
        model.allowInPersonPayments = false;
      } else if (prepayOption === 'prepayment') {
        model.requiresPartialDeposits = false;
        model.requiresPrepayment = true;
        model.allowInPersonPayments = false;
        // Clear deposit fields
        model.settings.depositType = null;
        model.settings.depositAmount = null;
        model.settings.depositPercentage = null;
      } else if (prepayOption === 'in-person') {
        model.requiresPartialDeposits = false;
        model.requiresPrepayment = false;
        model.allowInPersonPayments = true;
        // Clear deposit fields
        model.settings.depositType = null;
        model.settings.depositAmount = null;
        model.settings.depositPercentage = null;
      }
    }

    return this.props.saveUpdatedShop(model).catch((errors) => {
      this.setState({
        errors: errors.message,
      });
    });
  }

  render() {
    const paymentDepositsEnabled = this.props.shopLdFlags?.['payment-deposits-enabled'];

    const {
      handleSubmit,
      submitting,
      isReadOnlyAdmin,
      schedules,
      minInterval,
      daysOff,
      user,
      disabled,
      isLogoUploadAllowed,
      shopUser,
      isAdminShopForm,
      isPermissionPasscodeRequired,
      commissionSplitsEnabled,
      googleReviewEnabled,
      supportedLanguages,
      brandSupportedLanguages,
      defaultLanguage,
      initialValues,
      resetAutoPayoutSuggestionModal,
      settings,
    } = this.props;

    const { timezoneSuggestions, errors } = this.state;

    return (
      <form onSubmit={handleSubmit(trimFunction(this.saveBind))}>
        <fieldset disabled={disabled}>
          <div display-if={errors} className="u-margin-auto u-max-width-710">
            <div className="c-login-error-message u-mt">{errors}</div>
          </div>
          <div className="c-wrapper">
            <div className="u-color-white u-margin-auto u-max-width-710">
              <RoleAllowAdmin>
                <div className="c-wrapper u-pb0">
                  <div className="o-flexbox">
                    <div>
                      <CheckboxSwitch name="enabled" id="enabled" label="Enabled" wrapperClass="u-inline-block u-mr" />
                    </div>
                    <div>
                      <ImpersonateUser checkKioskUser shopId={this.props.initialValues.id} id={shopUser.id}>
                        <LoginButton />
                      </ImpersonateUser>
                      <span className="u-ml-">
                        <LogoutUserSessions id={shopUser.id} />
                      </span>
                    </div>
                  </div>

                  <div className="c-separator u-mv" />

                  <div className="c-heading c-heading--lightgrey">Fraud status</div>
                  <div>
                    <OptionInput
                      id="fraud-status-unverified"
                      className="u-mr"
                      name="verified"
                      parse={(val) => val === 'true'}
                      value={false}
                    >
                      Unverified, potentially fraudulent
                      <Tooltip>
                        This shop has not been verified and is blocked from transactions / deposits over $100. Newly
                        created shops receive this status by default.
                      </Tooltip>
                    </OptionInput>
                    <OptionInput id="fraud-status-verified" name="verified" value parse={(val) => val === 'true'}>
                      Verified non-fraudulent
                      <Tooltip>
                        This shop has been verified by SQUIRE Payments Team and can transact within normal Stripe
                        limits.
                      </Tooltip>
                    </OptionInput>
                  </div>

                  <div className="c-separator u-mt" />
                </div>
              </RoleAllowAdmin>

              <div className="layout c-wrapper u-pb0">
                <div className="layout__item u-1/3">
                  <div className="c-heading c-heading--lightgrey">Media</div>
                </div>
              </div>

              <div className="layout layout--middle">
                <div className="layout__item u-1/2-large-and-up">
                  <UploadAvatar name="avatar" who="shop's" />
                </div>
                <div className="layout__item u-1/2-large-and-up" display-if={isLogoUploadAllowed}>
                  <UploadLogo name="logo" />
                </div>
              </div>
              <div className="c-wrapper-horizontal">
                <div className="layout">
                  <RoleAllowAdmin>
                    <div className="layout__item u-1/1-medium-and-up">
                      <div className="u-position-relative">
                        <Field
                          name="brandName"
                          id="brandName"
                          change={this.props.change}
                          component={BrandField}
                          brandIdField="brandId"
                        />
                      </div>
                    </div>
                  </RoleAllowAdmin>
                  <div className="layout__item u-1/2-medium-and-up">
                    <div className="u-position-relative">
                      <div className="c-label-new">Shop name *</div>
                      <Input className="c-input" name="name" type="text" placeholder="Add name" />
                    </div>
                  </div>
                  <div className="layout__item u-1/2-medium-and-up">
                    <div className="u-position-relative">
                      <div className="c-label-new">Shop alias</div>
                      <Input className="c-input" name="alias" type="text" placeholder="Add alias" />
                    </div>
                  </div>
                  <div className="layout__item u-1/2-medium-and-up">
                    <div className="c-label-new">Website</div>
                    <Input className="c-input" name="website" type="text" placeholder="Add website" />
                  </div>
                  <div className="layout__item u-1/2-medium-and-up">
                    <div className="c-label-new">Phone</div>
                    <CountryCodePhoneInputRedux className="c-input" name="phone" placeholder="Add phone" />
                  </div>
                  <div className="layout__item u-1/2-medium-and-up">
                    <div className="u-position-relative">
                      <div className="c-label-new">Email</div>
                      <Input className="c-input" name="email" type="email" placeholder="Add email" />
                    </div>
                  </div>
                  <div className="layout__item u-1/2-medium-and-up">
                    <div className="c-label-new">Yelp ID</div>
                    <Input className="c-input" name="yelpID" type="text" placeholder="Add Yelp ID" />
                  </div>
                  <div className="layout__item u-1/2-medium-and-up">
                    <div className="c-label-new">Instagram</div>
                    <Input className="c-input" name="instagram" type="text" placeholder="Add Instagram Username" />
                  </div>
                  <div className="layout__item u-1/2-medium-and-up">
                    <Field name="timezone" component={this.timezoneField} timezoneSuggestions={timezoneSuggestions} />
                  </div>
                  <div className="layout__item u-1/2-medium-and-up">
                    <div className="c-label-new">Date Format</div>
                    <Select className="c-select" name="dateFormat" options={dateFormatOptions} />
                  </div>
                  <div className="layout__item u-1/2-medium-and-up">
                    <div className="c-label-new">Inventory Alert Email</div>
                    <Input
                      className="c-input"
                      name="inventoryAlertEmail"
                      type="email"
                      placeholder="Inventory Alert Email"
                    />
                  </div>
                  <div className="layout__item u-1/2-medium-and-up">
                    <div className="c-label-new">Inventory Alert Phone</div>
                    <CountryCodePhoneInputRedux
                      className="c-input"
                      name="inventoryAlertPhone"
                      placeholder="Inventory Alert Phone"
                    />
                  </div>
                  <div className="layout__item u-1/2-medium-and-up">
                    <div className="c-label-new">Default Cash Drawer Balance</div>
                    <CurrencyInput
                      className="c-input"
                      placeholder="Default Cash Drawer Balance"
                      name="defaultCashDrawerBalance"
                      currency={this.props.currency}
                    />
                  </div>
                  <PermissionPasscode
                    display-if={!isAdminShopForm && isPermissionPasscodeRequired}
                    wrapperClass="layout__item u-1/2-medium-and-up"
                  />
                  <RoleAllowAdmin wrapperClass="layout__item u-1/2-medium-and-up">
                    <div className="u-position-relative">
                      <div className="c-label-new">Type</div>
                      <Field component="select" name="kind" className="c-select">
                        <option value={SHOP_TYPE.commission}>Commission</option>
                        <option value={SHOP_TYPE.rental}>Rental</option>
                      </Field>
                    </div>
                  </RoleAllowAdmin>
                  <RoleAllowAdmin wrapperClass="layout__item u-1/2-medium-and-up">
                    <div className="u-position-relative">
                      <div className="c-label-new">Currency</div>
                      <CurrencySelect name="currency" />
                    </div>
                  </RoleAllowAdmin>
                  <RoleAllowAdmin wrapperClass="layout__item u-1/2-medium-and-up">
                    <div className="u-position-relative">
                      <div className="c-label c-label--light">Reader Location ID</div>
                      <Input type="text" className="c-input" name="readerLocationId" placeholder="Reader Location ID" />
                    </div>
                  </RoleAllowAdmin>
                  <div className="layout__item">
                    <div className="c-label-new">Shop description</div>
                    <TextArea className="c-textarea" name="heading" placeholder="Shop description" />
                  </div>
                  {isAdminShopForm && (
                    <GoogleReviewsSection googleReviewEnabled={googleReviewEnabled} settings={settings} />
                  )}
                  <NotificationMethods shop />
                </div>
              </div>
              <RoleDenyAdmin>
                <div className="c-separator u-mb u-mt" />
                <Schedules schedules={schedules} minInterval={minInterval} />
                <div className="c-separator u-mb" />
                <Days days={daysOff} type="off" />
              </RoleDenyAdmin>
              <div className="c-separator u-mb u-mt+" />
              <AddressForm country={this.props.initialValues.address.country} />
              <div className="c-separator u-mb u-mt" />
              <div className="c-heading c-heading--lightgrey c-wrapper u-pv0 u-mb-">Language Settings</div>
              <ShopLanguageSettings
                defaultLanguage={defaultLanguage}
                supportedLanguages={supportedLanguages}
                brandSupportedLanguages={brandSupportedLanguages}
                change={this.props.change}
                initialValues={initialValues}
              />
              <div className="c-separator u-mb u-mt" />
              <div className="c-heading c-heading--lightgrey c-wrapper u-pv0 u-mb-">Options</div>
              <div className="c-wrapper-horizontal u-pb--">
                <div className="layout">
                  <div className="layout__item u-1/2 u-pl0">
                    <CheckboxSwitch name="adultsOnly" id="adultsOnly" label="21+ only" wrapperClass="layout__item" />
                    <CheckboxSwitch
                      name="allowBookWithAnyBarber"
                      id="allowBookWithAnyBarber"
                      label="Allow booking with any barber"
                      wrapperClass="layout__item"
                    />
                    <CheckboxSwitch
                      name="grossUpFees"
                      id="grossUpFees"
                      label="Gross Up Fees"
                      wrapperClass="layout__item"
                    />
                    <CheckboxSwitch
                      display-if={this.props[flagNames.GROUP_APPOINTMENTS] || isAdminShopForm}
                      name="settings.groupAppointmentsEnabled"
                      id="settings.groupAppointmentsEnabled"
                      label={
                        <>
                          Group Appointments&nbsp;
                          <Tooltip>
                            Enables a group of people to book multiple appointments at once on the client side
                          </Tooltip>
                        </>
                      }
                      wrapperClass="layout__item"
                    />
                    <CheckboxSwitch
                      display-if={this.props[flagNames.CLIENT_REVIEWS] || isAdminShopForm}
                      name="settings.clientReviewsEnabled"
                      id="settings.clientReviewsEnabled"
                      label={
                        <>
                          Client Reviews&nbsp;
                          <Tooltip>Allows clients to provide feedback after receiving a service.</Tooltip>
                        </>
                      }
                      wrapperClass="layout__item"
                    />
                    <div className="layout__item" display-if={!commissionSplitsEnabled}>
                      <span className="c-label-new">Payout Discount Mode</span>
                      <Select
                        className="c-select"
                        name="settings.payoutDiscountSplit"
                        options={DISCOUNT_PAYOUT_OPTIONS}
                      />
                    </div>
                    <RoleAllowAdmin>
                      <CheckboxSwitch
                        name="legacyCommanderUser"
                        id="legacyCommanderUser"
                        label="Legacy commander user"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        name="waitingListEnabled"
                        id="waitingListEnabled"
                        label="Waiting list"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        name="rewardsEnabled"
                        id="rewardsEnabled"
                        label="Loyalty program"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        name="kioskEnabled"
                        id="kioskEnabled"
                        label="Squire Booking (Kiosk Mode)"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        name="cashDrawerEnabled"
                        id="cashDrawerEnabled"
                        label="Cash Drawer"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        name="squireEcosystem"
                        id="squireEcosystem"
                        label="Squire Ecosystem"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        name="googleEcosystem"
                        id="googleEcosystem"
                        label="Google Ecosystem"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        name="bookingCodeEnabled"
                        id="bookingCodeEnabled"
                        label="Booking Codes"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        name="noExtrasForBookNoPay"
                        id="noExtrasForBookNoPay"
                        label="No Fee/Tip on Reserve Page"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        name="requiresWaiver"
                        id="requiresWaiver"
                        label="Requires Waiver"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        name="isFrenchWaiver"
                        id="isFrenchWaiver"
                        label="French Waiver"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        name="useQuickBooks"
                        id="useQuickBooks"
                        label="Quickbooks integration"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        name="mobileAppCta"
                        id="mobileAppCta"
                        label="Include Mobile App CTAs"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        name="settings.memberships"
                        id="memberships"
                        label="Memberships"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        name="settings.apiReader"
                        id="apiReaderEnabled"
                        label="Enable API-based Readers"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        name="settings.paymentDepositsEnabled"
                        id="paymentDepositsEnabled"
                        label="Booking Deposits"
                        wrapperClass="layout__item"
                      />
                    </RoleAllowAdmin>
                  </div>
                  <div className="layout__item u-1/2 u-pl0">
                    <RoleAllowAdmin>
                      <CheckboxSwitch
                        name="sendEmailReports"
                        id="sendEmailReports"
                        label="Send email reports"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        name="sendMonthlyFeesReport"
                        id="sendMonthlyFeesReport"
                        label="Send Monthly Fees Report"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        name="readerlessPos"
                        id="readerlessPos"
                        label="Third Party POS"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch name="webReader" id="webReader" label="Web Reader" wrapperClass="layout__item" />
                      <CheckboxSwitch
                        name="settings.webReaderTipping"
                        id="settings.webReaderTipping"
                        wrapperClass="layout__item"
                        label={
                          <span>
                            Web Reader Tipping
                            <Tooltip className="u-type--no-transform">
                              Tip options will appear on WisePOS E during checkout from Web and iPad Native. Does not
                              support Round Up Tips or transactions with more than one service provider.
                            </Tooltip>
                          </span>
                        }
                      />
                      <CheckboxSwitch
                        name="noDefaultTip"
                        id="noDefaultTip"
                        label="No Default Tip"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        name="cashTipsEnabled"
                        id="cashTipsEnabled"
                        label="Cash Tips"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        name="readerTipsEnabled"
                        id="readerTipsEnabled"
                        label="POS Tips"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        name="giftCardTipsEnabled"
                        id="giftCardTipsEnabled"
                        label="Gift Card Tips"
                        wrapperClass="layout__item"
                      />
                    </RoleAllowAdmin>
                    <CheckboxSwitch
                      name="tipOnProduct"
                      id="tipOnProduct"
                      label="Use Prod. Price in Calc. Tips"
                      wrapperClass="layout__item"
                    />
                    <CheckboxSwitch
                      name="includeTaxInTipCalculation"
                      id="includeTaxInTipCalculation"
                      label="Use Taxes in Calculation of Tips"
                      wrapperClass="layout__item"
                    />
                    <RoleAllowAdmin>
                      <CheckboxSwitch
                        name="settings.cashOutTipsShortcutEnabled"
                        id="cashOutTipsShortcutEnabled"
                        label="Cash Out Tips Shortcut"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        name="splitPaymentsEnabled"
                        id="splitPaymentsEnabled"
                        label="Split Payments"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        name="chargeNoShows"
                        id="chargeNoShows"
                        label="Charge no shows"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        display-if={this.props.isRental}
                        name="productSalesToShop"
                        id="productSalesToShop"
                        label="Products to Shop Account"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        name="walkInOnly"
                        id="walkInOnly"
                        label="Walk-In Only"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        name="giftCardEnabled"
                        id="giftCardEnabled"
                        label="Gift cards"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        name="customerGiftCardEnabled"
                        id="customerGiftCardEnabled"
                        label="E-Commerce Gift Card Sales"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        name="barberSpecificGC"
                        id="barberSpecificGC"
                        label="Barber specific GCs"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        name="sendGCNotificationsToCommissionBarbers"
                        id="sendGCNotificationsToCommissionBarbers"
                        label="GC Message to Commission Barber"
                        wrapperClass="layout__item u-nowrap"
                        display-if={!this.props.isRental}
                      />
                      <CheckboxSwitch
                        name="settings.cashRegisterCountBillsDefault"
                        id="cashRegisterCountBillsDefault"
                        label="Count Bills Default(US,CA)"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        name="timeTrackingEnabled"
                        id="timeTrackingEnabled"
                        label="Time tracking enabled"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        name="settings.extendedHourlyWages"
                        id="extendedHourlyWages"
                        label="Extended hourly wages"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        name="settings.forceClockInBeforeSale"
                        id="forceClockInBeforeSale"
                        label="Force Clock-in Before Sale"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        name="permissionPasscodeEnabled"
                        id="permissionPasscodeEnabled"
                        label="Permission passcode enabled"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        name="calculateTaxFromNetAmount"
                        id="calculateTaxFromNetAmount"
                        label="Calculate tax from net amount"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        name="includeChargeLinkInNotification"
                        id="includeChargeLinkInNotification"
                        label="Include charge link in reminder"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        name="applyCNPRateToChargeLink"
                        id="applyCNPRateToChargeLink"
                        label="Apply CNP Rate To Charge Link"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        name="settings.powerBooking"
                        id="powerBooking"
                        label="Enable power booking"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        display-if={this.props[flagNames.WALK_IN_LIST]}
                        name="settings.walkInListEnabled"
                        id="walkInListEnabled"
                        label="Enable walk-in list"
                        wrapperClass="layout__item"
                      />
                      <CheckboxSwitch
                        display-if={this.props[flagNames.WALK_IN_LIST]}
                        name="settings.canCustomerEnrollToWalkInList"
                        id="canCustomerEnrollToWalkInList"
                        label="Walk-ins can add themselves"
                        wrapperClass="layout__item"
                      />
                      <WalkInListUrlDisplay
                        display-if={this.props[flagNames.WALK_IN_LIST] && this.props.canCustomerEnrollToWalkInList}
                        url={`https://waitlist-mobile-qa.getsquire.com/booking/book/${this.props.initialValues?.id || ''}/walkin-list`}
                      />
                    </RoleAllowAdmin>
                    <CheckboxSwitch
                      display-if={this.props.isRental}
                      name="settings.flexCtaOnCom"
                      id="flexCtaOnCom"
                      label={
                        <>
                          Allow Flex setup
                          <Tooltip>
                            When this is enabled, barbers at your shop will be able to set up a custom Flex booking app
                            on their mobile Commander app.
                          </Tooltip>
                        </>
                      }
                      wrapperClass="layout__item"
                    />
                  </div>
                </div>
                <RoleAllowAdmin>
                  <div className="layout">
                    <div className="layout__item">
                      <div className="c-label-new">Minimum interval</div>
                      <Select
                        options={[
                          {
                            label: '15 mins',
                            value: 15,
                          },
                          {
                            label: '5 mins',
                            value: 5,
                          },
                        ]}
                        name="minInterval"
                        normalize={(val) => Number(val)}
                        className="c-select u-3/5 u-2/5-extralarge-and-up u-mb0"
                      />
                    </div>
                  </div>
                </RoleAllowAdmin>
                <RoleAllowAdmin display-if={this.props.kioskEnabled}>
                  <div className="layout u-mt">
                    <div className="layout__item">
                      <div className="c-label-new">Kiosk Booking Prepay Threshold</div>
                      <Select
                        options={prepayOnlyThresholdOptions}
                        name="prepayOnlyThreshold"
                        normalize={(val) => Number(val)}
                        className="c-select u-3/5 u-2/5-extralarge-and-up u-mb0"
                      />
                    </div>
                  </div>
                </RoleAllowAdmin>
              </div>
              <RoleAllowAdmin>
                <div className="c-separator u-mb u-mt" />
                <div className="c-heading c-heading--lightgrey c-wrapper u-pv0 u-mb-">Override barber settings</div>
                <div className="c-wrapper-horizontal u-pb--">
                  <div className="layout">
                    <CheckboxSwitch
                      name="overrideBarberSettings"
                      id="overrideBarberSettings"
                      label="Override barber settings"
                      wrapperClass="layout__item"
                    />
                  </div>
                </div>
                {paymentDepositsEnabled && <PrepaymentSettingsContainer />}
                <BarberSettings
                  user={user}
                  currency={this.props.currency}
                  canCustomerCancel={this.props.canCustomerCancel}
                  paymentDepositsEnabled={paymentDepositsEnabled}
                />
              </RoleAllowAdmin>
              <RoleAllowAdmin display-if={!this.props.isRental}>
                <div className="c-separator u-mb u-mt" />
                <CommissionSplit payrollKind={this.props.payrollKind} payrollEnabled={this.props.payrollEnabled} />
              </RoleAllowAdmin>
              <RoleAllowAdmin display-if={!!this.props.initialValues.id && config.deploymentEnv !== 'production'}>
                <div className="c-wrapper-horizontal u-pt u-pb--">
                  <button
                    type="button"
                    onClick={resetAutoPayoutSuggestionModal}
                    className="c-btn"
                    data-automation-id="reset-auto-payout-suggestion-button"
                  >
                    Reset Auto Payout Suggestion Modal State
                  </button>
                </div>
              </RoleAllowAdmin>
              <div className="c-separator u-mv" />
              <div className="c-heading c-heading--lightgrey c-wrapper u-pv0 u-mb-">Tips</div>
              <div className="c-wrapper-horizontal u-pb+">
                <div className="u-flex o-flexbox--justify-space-between u-align-items-center u-mb-">
                  <div className="u-fontsize-small u-type--bold">Percentage Tiers</div>
                  <div className="u-flex u-align-items-center">
                    <CheckboxSwitch name="roundUpTips" id="roundUpTips" label="Round up tips" />
                    <div className="u-position-relative" style={{ top: '1px' }}>
                      <Tooltip className="c-info-tooltip-new">
                        Round up tips to the nearest dollar and omit the percentage symbol from tip screens.
                      </Tooltip>
                    </div>
                  </div>
                </div>
                <div className="layout">
                  <div className="layout__item u-1/4">
                    <div className="c-label-new">Tier 1</div>
                    <Input className="c-input" name="tipTier1" type="number" prefix="%" />
                  </div>
                  <div className="layout__item u-1/4">
                    <div className="c-label-new">Tier 2</div>
                    <Input className="c-input" name="tipTier2" type="number" prefix="%" />
                  </div>
                  <div className="layout__item u-1/4">
                    <div className="c-label-new">Tier 3</div>
                    <Input className="c-input" name="tipTier3" type="number" prefix="%" />
                  </div>
                  <div className="layout__item u-1/4">
                    <div className="c-label-new">Tier 4</div>
                    <Input className="c-input" name="tipTier4" type="number" prefix="%" />
                  </div>
                </div>
                <div className="u-flex u-align-items-center u-mt-">
                  <div className="u-fontsize-small">Use Percentage Tiers Above</div>
                  <Tooltip className="c-info-tooltip-new">
                    Tip options across SQUIRE will reflect Percentage Tiers when the tip-eligible sale amount is greater
                    than or equal to this value.
                  </Tooltip>
                  <div className="u-1/4 u-ml-">
                    <CurrencyInput className="c-input" name="flatTipsThreshold" />
                  </div>
                </div>
                <div className="u-mt u-mb- u-fontsize-small u-type--bold">Flat Tiers</div>
                <div className="layout">
                  <div className="layout__item u-1/4">
                    <div className="c-label-new">Tier 1</div>
                    <CurrencyInput className="c-input" name="flatTipTier1" />
                  </div>
                  <div className="layout__item u-1/4">
                    <div className="c-label-new">Tier 2</div>
                    <CurrencyInput className="c-input" name="flatTipTier2" />
                  </div>
                  <div className="layout__item u-1/4">
                    <div className="c-label-new">Tier 3</div>
                    <CurrencyInput className="c-input" name="flatTipTier3" />
                  </div>
                  <div className="layout__item u-1/4">
                    <div className="c-label-new">Tier 4</div>
                    <CurrencyInput className="c-input" name="flatTipTier4" />
                  </div>
                </div>
                <div className="c-separator u-mv" />
                <div className="layout">
                  <div className="layout__item u-7/8">
                    <div className="u-fontsize-small u-type--bold">Booking Tip</div>
                    <div className="u-fontsize-small">Displays tipping during the booking flow</div>
                  </div>
                  <div className="layout__item u-1/8">
                    <CheckboxSwitch name="tipEnabled" id="tipEnabled" />
                  </div>
                </div>
                <div className="c-separator u-mv" />
                <div className="layout">
                  <div className="layout__item u-7/8">
                    <div className="u-fontsize-small u-type--bold">Confirmation Tip</div>
                    <div className="u-fontsize-small">Displays tipping on the confirmation page</div>
                  </div>
                  <div className="layout__item u-1/8">
                    <CheckboxSwitch name="lateTipping" id="lateTipping" />
                  </div>
                </div>
              </div>
              <div className="u-text-right u-p u-pt0">
                <CancelButtonLink link={this.props.cancelHref} content="Cancel" />
                <SaveButton content="Save" disabled={submitting || isReadOnlyAdmin} />
              </div>
            </div>
          </div>
        </fieldset>
      </form>
    );
  }
}

const form = reduxForm({
  form: 'editShop',
  validate,
  enableReinitialize: true,
  touchOnSubmit: true,
});

const selector = formValueSelector('editShop');

const mapStateToProps = (state) => {
  const brand = getBrand(state, selector(state, 'brandId'));
  const brandSupportedLanguages =
    selector(state, 'brandSupportedLanguages') || getLanguageOptionsByCodes(get(brand, 'settings.supportedLanguages'));

  return {
    schedules: selector(state, 'schedules'),
    daysOff: selector(state, 'days_off'),
    canCustomerCancel: selector(state, 'canCustomerCancel'),
    minInterval: activeShopMinInterval(state),
    dailyPayroll: activeShopDailyPayrollEnabled(state),
    commissionSplits: activeShopCommissionSplitsEnabled(state),
    isReadOnlyAdmin: getIsReadOnlyAdminUser(state),
    user: state.auth.user,
    isLogoUploadAllowed: getIsAdminRole(state) || getIsLogoUploadAllowed(selector(state, 'subscription')),
    currency: selector(state, 'currency'),
    kioskEnabled: selector(state, 'kioskEnabled'),
    isRental: parseInt(selector(state, 'kind'), 10) === SHOP_TYPE.rental,
    isAdminShopForm: getIsAdminRole(state),
    isPermissionPasscodeRequired: getIsPermissionPasscodeRequired(state),
    payrollKind: selector(state, 'payrollKind'),
    extendedHourlyWages: selector(state, 'settings.extendedHourlyWages'),
    payrollEnabled: selector(state, 'payrollEnabled'),
    giftCardEnabled: selector(state, 'giftCardEnabled'),
    customerGiftCardEnabled: selector(state, 'customerGiftCardEnabled'),
    barberSpecificGC: selector(state, 'barberSpecificGC'),
    sendGCNotificationsToCommissionBarbers: selector(state, 'sendGCNotificationsToCommissionBarbers'),
    requiresWaiver: selector(state, 'requiresWaiver'),
    isFrenchWaiver: selector(state, 'isFrenchWaiver'),
    commissionSplitsEnabled:
      selector(state, 'payrollEnabled') && selector(state, 'payrollKind') === PAYROLL_KINDS.commissionSplits,
    googleReviewEnabled: selector(state, 'settings.googleReviewEnabled'),
    supportedLanguages: selector(state, 'settings.supportedLanguages'),
    defaultLanguage: selector(state, 'settings.defaultLanguage'),
    brand,
    brandSupportedLanguages: [brandSupportedLanguagesOption, ...brandSupportedLanguages],
    settings: selector(state, 'settings'),
    canCustomerEnrollToWalkInList: selector(state, 'settings.canCustomerEnrollToWalkInList'),
  };
};

export default compose(
  connect(mapStateToProps),
  form,
  withPropsOnChange(['payrollKind', 'payrollEnabled'], (props) => {
    const dailyPayroll = props.payrollEnabled && props.payrollKind === PAYROLL_KINDS.dailyPayroll;
    const commissionSplits = props.payrollEnabled && props.payrollKind === PAYROLL_KINDS.commissionSplits;
    props.change(PAYROLL_KINDS.dailyPayroll, dailyPayroll);
    props.change(PAYROLL_KINDS.commissionSplits, commissionSplits);
  }),
  withPropsOnChange(
    ['giftCardEnabled', 'customerGiftCardEnabled', 'barberSpecificGC', 'sendGCNotificationsToCommissionBarbers'],
    (props) => {
      if (!props.giftCardEnabled) {
        if (props.customerGiftCardEnabled) {
          props.change('customerGiftCardEnabled', false);
        }
      }

      if (!props.isRental && props.sendGCNotificationsToCommissionBarbers) {
        props.change('sendGCNotificationsToCommissionBarbers', false);
      }

      if ((!props.customerGiftCardEnabled || !props.isRental) && props.barberSpecificGC) {
        props.change('barberSpecificGC', false);
      }

      if (props.isRental && props.customerGiftCardEnabled) {
        props.change('barberSpecificGC', true);
      }
    }
  ),
  withPropsOnChange(['isRental'], (props) => {
    if (!props.isRental) {
      props.change('barberSpecificGC', false);
      props.change('sendGCNotificationsToCommissionBarbers', false);
    }
  }),
  withPropsOnChange(['requiresWaiver', 'isFrenchWaiver'], (props) => {
    if (!props.requiresWaiver && !isUndefined(props.requiresWaiver)) {
      props.change('isFrenchWaiver', false);
    }
  }),
  withPropsOnChange(
    (prevProps, nextProps) => prevProps.isRental && !nextProps.isRental,
    (props) => {
      // doing it here instead of in the section above (withPropsOnChange('isRental'))
      // to make sure it's only triggered when the shop type is actually toggled by the user
      if (props.initialized && !props.isRental) {
        props.change('settings.flexCtaOnCom', false);
      }
    }
  ),
  withHandlers({
    saveUpdatedShop: (props) => (dataFromSave) => {
      const settings = dataFromSave.settings || {};

      if (
        dataFromSave.settings.supportedLanguages &&
        dataFromSave.settings.supportedLanguages.includes(brandSupportedLanguagesOption)
      ) {
        settings.supportedLanguages = null;
      } else if (dataFromSave.settings.supportedLanguages) {
        settings.supportedLanguages = dataFromSave.settings.supportedLanguages.map(({ value }) => value);
      }

      settings.defaultLanguage = settings.defaultLanguage ? settings.defaultLanguage.value : null;

      return props.saveUpdatedShop({ ...dataFromSave, settings });
    },
    resetAutoPayoutSuggestionModal: (props) => () => {
      resetAutoPayoutSuggestionModalState(props.initialValues.id);
    },
  }),
  withInjectFeatureFlagsToProps([
    flagNames.AUTOPAYOUT_SELF_SERVE,
    flagNames.GROUP_APPOINTMENTS,
    flagNames.CLIENT_REVIEWS,
    flagNames.WALK_IN_LIST,
    flagNames.PAYMENT_DEPOSITS_ENABLED,
  ])
)(ShopForm);
